import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, TextField, Table, TableBody, TableCell, 
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, 
  DialogActions, Select, MenuItem, FormControl, InputLabel, IconButton,
  Chip, InputAdornment
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';

function References() {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Form State
  const [formData, setFormData] = useState({
    type: 'Out',
    subject: '',
    recipient_to: '',
    address: '',
    description: '',
    status: 'Pending'
  });

  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('user_id');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchReferences = async () => {
    setLoading(true);
    try {
      const params = { user_id: userId };
      if (search) params.search = search;
      
      const res = await axios.get(`${API_URL}/references`, { headers, params });
      setReferences(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferences();
  }, [search]);

  const handleCreate = async () => {
    if (!formData.subject || !formData.type) {
        alert("Please fill in required fields (Type, Subject)");
        return;
    }

    setCreateLoading(true);
    try {
      await axios.post(`${API_URL}/references`, {
        user_id: userId,
        ...formData
      }, { headers });
      
      setOpen(false);
      setFormData({
        type: 'Out',
        subject: '',
        recipient_to: '',
        address: '',
        description: '',
        status: 'Pending'
      });
      fetchReferences();
    } catch (err) {
      console.error(err);
      alert("Error creating reference");
    } finally {
      setCreateLoading(false);
    }
  };

  const getStatusColor = (status) => {
      switch(status) {
          case 'Approved': return 'success';
          case 'Pending': return 'warning';
          case 'Rejected': return 'error';
          default: return 'default';
      }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField 
                size="small" 
                placeholder="Search references..." 
                value={search} 
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
                }}
                sx={{ bgcolor: 'white', minWidth: 300 }}
            />
            <Button variant="outlined" onClick={fetchReferences}>Search</Button>
        </Box>
      
        <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => setOpen(true)}
            sx={{ borderRadius: 2, px: 3 }}
        >
            Create Reference
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        <Table>
          <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Ref Number</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Subject</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>To</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {references.map((ref) => (
              <TableRow key={ref.id} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'medium' }}>{ref.ref_number}</TableCell>
                <TableCell>
                    <Chip label={ref.type} size="small" color={ref.type === 'In' ? 'info' : ref.type === 'Out' ? 'secondary' : 'default'} variant="outlined" />
                </TableCell>
                <TableCell>{ref.subject}</TableCell>
                <TableCell>{ref.recipient_to || '-'}</TableCell>
                <TableCell>
                    <Chip label={ref.status} size="small" color={getStatusColor(ref.status)} variant="outlined" />
                </TableCell>
                <TableCell>{ref.created_at}</TableCell>
                <TableCell align="right">
                  <IconButton color="primary" onClick={() => navigate(`/references/${ref.id}`)} size="small">
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {references.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No references found. Create one to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Reference</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                <MenuItem value="In">In</MenuItem>
                <MenuItem value="Out">Out</MenuItem>
                <MenuItem value="Certification">Certification</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Subject"
              fullWidth
              required
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            />

            <TextField
              label="To (Recipient)"
              fullWidth
              value={formData.recipient_to}
              onChange={(e) => setFormData({ ...formData, recipient_to: e.target.value })}
            />

            <TextField
              label="Address"
              fullWidth
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={createLoading}>
            {createLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default References;
